// noinspection ExceptionCaughtLocallyJS

import _Vue from "vue";
import { Auth as _Auth } from "aws-amplify";
import { SignOutOpts } from "@aws-amplify/auth/lib/types";
import { CognitoUserSession, CognitoUser } from "amazon-cognito-identity-js";
import router from "@/router";

const useMemoryStorage = () => {
  const state = new Map<string, string>();

  const getItem = (key: string) => state.get(key) || null;
  const setItem = (key: string, value: string) => {
    state.set(key, value);
  };
  const removeItem = (key: string) => {
    state.delete(key);
  };
  const clear = () => state.clear();

  return { getItem, setItem, removeItem, clear };
};

if (process.env.VUE_APP_COGNITO_ENABLED === "1") {
  _Auth.configure({
    region: process.env.VUE_APP_COGNITO_REGION,
    userPoolId: process.env.VUE_APP_COGNITO_USER_POOL_ID,
    userPoolWebClientId: process.env.VUE_APP_COGNITO_USER_POOL_WEB_CLIENT_ID,
    storage: useMemoryStorage()
  });
}

export interface AuthManager {
  enabled: boolean;
  session: () => Promise<CognitoUserSession>;
  user: () => Promise<any>;
  signIn: (username: string, password: string) => Promise<CognitoUser | any>;
  signOut: (opts: SignOutOpts) => Promise<any>;
  groups: () => Promise<string[]>;
  headers: () => Promise<any>;
  raw: typeof _Auth;
}

let lock = false;

export const delay = (time: number) =>
  time === 0
    ? Promise.resolve()
    : new Promise<void>(resolve => {
        setTimeout(() => resolve(), time);
      });

export const retry = <T>(
  functor: (iteration: number) => Promise<T>,
  tries = 1,
  condition?: (exception: unknown) => boolean
): Promise<T> => {
  for (let current = 1; current < tries; current++) {
    try {
      return functor(current - 1);
    } catch (exception) {
      if (condition && !condition(exception)) throw exception;
    }
  }

  return functor(tries - 1);
};

export const auth: AuthManager = {
  enabled: process.env.VUE_APP_COGNITO_ENABLED === "1",
  session: async () => {
    try {
      const session = await retry(
        it => delay(it * 500).then(() => _Auth.currentSession()),
        11,
        error =>
          error instanceof Error &&
          error.message.toLocaleLowerCase().includes("network error")
      );
      if (!session.isValid()) throw new Error();
      return session;
    } catch (e) {
      if (!lock) {
        lock = true;
        await router.push("/login");
        lock = false;
      }
      throw e;
    }
  },
  user: () => _Auth.currentUserInfo(),
  signIn: (username: string, password: string) =>
    _Auth.signIn(username, password),
  signOut: (opts: SignOutOpts) => _Auth.signOut(opts),
  groups: async () => {
    return (
      (await _Auth.currentSession()).getIdToken().decodePayload()[
        "cognito:groups"
      ] || []
    );
  },
  headers: async () => {
    return {
      Authorization: (await auth.session()).getIdToken().getJwtToken()
    };
  },
  raw: _Auth
};

export default function AuthPlugin(Vue: typeof _Vue, options?: any): void {
  Vue.prototype.$auth = auth;
}
